import React, { useEffect, useState, useCallback } from "react";
import { Toaster, toast } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Pill, Button, Input } from "../../components/common/Primitives";
import api, { formatApiError } from "../../lib/api";
import { formatDate } from "../../lib/datetime";
import { Ticket, Plus, Trash2, X, Save, Copy } from "lucide-react";

export default function AdminCoupons() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/coupons");
      setItems(r.data?.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const emptyCoupon = () => ({
    code: "", discount_type: "percent", value: 10,
    description: "", min_order_inr: "", max_discount_inr: "",
    valid_from: "", valid_to: "",
    allowed_roles: [], allowed_plans: [], audience: "",
    max_uses: "", max_uses_per_user: 1, is_active: true,
  });

  const save = async () => {
    setBusy(true);
    try {
      const draft = editing;
      const body = {
        code: (draft.code || "").toUpperCase().trim(),
        discount_type: draft.discount_type,
        value: Number(draft.value),
        description: draft.description || null,
        min_order_inr: draft.min_order_inr ? Number(draft.min_order_inr) : null,
        max_discount_inr: draft.max_discount_inr ? Number(draft.max_discount_inr) : null,
        valid_from: draft.valid_from || null,
        valid_to: draft.valid_to || null,
        allowed_roles: (draft.allowed_roles || []).length ? draft.allowed_roles : null,
        allowed_plans: (draft.allowed_plans || []).length ? draft.allowed_plans : null,
        audience: draft.audience || null,
        max_uses: draft.max_uses ? Number(draft.max_uses) : null,
        max_uses_per_user: draft.max_uses_per_user ? Number(draft.max_uses_per_user) : null,
        is_active: !!draft.is_active,
      };
      if (draft.coupon_id) {
        await api.patch(`/admin/coupons/${draft.coupon_id}`, body);
        toast.success("Coupon updated");
      } else {
        await api.post("/admin/coupons", body);
        toast.success("Coupon created");
      }
      setEditing(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this coupon permanently?")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/coupons/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <DashboardShell>
      <Toaster richColors position="top-right" />
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="overline">BILLING</div>
            <h1 className="font-display text-3xl lg:text-4xl mt-1">Discount coupons</h1>
            <div className="text-sm text-ink-soft mt-1">
              Flexible codes — percent or flat, generic or user-specific, time-boxed and usage-capped.
            </div>
          </div>
          <Button variant="gold" onClick={() => setEditing(emptyCoupon())} data-testid="coupon-add">
            <Plus size={14} className="inline mr-1" /> New coupon
          </Button>
        </div>

        <Card testId="coupons-list">
          {loading ? (
            <div className="text-sm text-ink-muted italic py-4">Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-sm text-ink-muted italic py-4" data-testid="coupons-empty">
              No coupons yet. Create your first one to run a promo or reward a specific user.
            </div>
          ) : (
            <div className="divide-y divide-navy/[0.06]">
              {items.map((c) => (
                <div key={c.coupon_id} className="py-3 flex items-center justify-between gap-3" data-testid={`coupon-${c.coupon_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-mono font-bold text-emerald-800 text-sm">{c.code}</span>
                      <Pill tone={c.is_active ? "success" : "muted"}>
                        {c.is_active ? "Active" : "Inactive"}
                      </Pill>
                      <Pill tone="gold">
                        {c.discount_type === "percent" ? `${c.value}% off` : `₹${c.value} off`}
                      </Pill>
                      {c.max_uses && (
                        <span className="text-[10px] font-mono text-ink-muted">
                          {c.uses_count || 0} / {c.max_uses} used
                        </span>
                      )}
                    </div>
                    {c.description && <div className="text-xs text-ink-soft">{c.description}</div>}
                    <div className="text-[10px] font-mono text-ink-muted mt-0.5">
                      {c.valid_from && <>from {formatDate(c.valid_from)} · </>}
                      {c.valid_to && <>until {formatDate(c.valid_to)}</>}
                      {c.audience && <> · audience: {c.audience}</>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => { navigator.clipboard.writeText(c.code); toast.success("Copied"); }}
                      className="px-2 py-1 rounded border border-navy/15 text-[11px] font-heading hover:border-emerald-500"
                      data-testid={`coupon-copy-${c.coupon_id}`} title="Copy code">
                      <Copy size={12} />
                    </button>
                    <button onClick={() => setEditing({ ...c, allowed_roles: c.allowed_roles || [], allowed_plans: c.allowed_plans || [] })}
                      className="px-2 py-1 rounded border border-navy/15 text-[11px] font-heading hover:border-emerald-500"
                      data-testid={`coupon-edit-${c.coupon_id}`}>Edit</button>
                    <button onClick={() => remove(c.coupon_id)}
                      className="px-2 py-1 rounded border border-rose-300 text-[11px] font-heading text-rose-700 hover:bg-rose-50"
                      data-testid={`coupon-delete-${c.coupon_id}`}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {editing && (
        <CouponEditor
          draft={editing}
          onChange={setEditing}
          onSave={save}
          onCancel={() => setEditing(null)}
          busy={busy}
        />
      )}
    </DashboardShell>
  );
}

function CouponEditor({ draft, onChange, onSave, onCancel, busy }) {
  const patch = (k) => (e) => onChange({ ...draft, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="coupon-editor">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between border-b border-navy/10 px-6 py-4">
          <div>
            <div className="overline flex items-center gap-1">
              <Ticket size={12} /> {draft.coupon_id ? "Edit coupon" : "New coupon"}
            </div>
          </div>
          <button onClick={onCancel} className="text-ink-muted hover:text-ink"
            data-testid="coupon-editor-close">
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-3 text-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input label="Code (uppercase)" value={draft.code || ""}
              onChange={(e) => onChange({ ...draft, code: e.target.value.toUpperCase() })}
              testId="coupon-code" />
            <div>
              <label className="text-[11px] font-heading uppercase tracking-widest text-ink-muted">Discount type</label>
              <select value={draft.discount_type} onChange={patch("discount_type")}
                className="mt-1 w-full px-3 py-2 rounded border border-navy/15 text-sm bg-white"
                data-testid="coupon-type">
                <option value="percent">Percent (%)</option>
                <option value="flat">Flat (INR)</option>
              </select>
            </div>
            <Input label={draft.discount_type === "percent" ? "Percent value" : "Flat INR"}
              type="number" value={draft.value} onChange={patch("value")}
              testId="coupon-value" />
            <Input label="Description" value={draft.description || ""}
              onChange={patch("description")} testId="coupon-desc" />
            <Input label="Min order (₹) — optional" type="number"
              value={draft.min_order_inr || ""} onChange={patch("min_order_inr")}
              testId="coupon-min" />
            <Input label="Max discount cap (₹) — optional" type="number"
              value={draft.max_discount_inr || ""} onChange={patch("max_discount_inr")}
              testId="coupon-cap" />
            <Input label="Valid from (ISO)" type="datetime-local"
              value={(draft.valid_from || "").slice(0, 16)}
              onChange={(e) => onChange({ ...draft, valid_from: e.target.value ? new Date(e.target.value).toISOString() : "" })}
              testId="coupon-from" />
            <Input label="Valid until (ISO)" type="datetime-local"
              value={(draft.valid_to || "").slice(0, 16)}
              onChange={(e) => onChange({ ...draft, valid_to: e.target.value ? new Date(e.target.value).toISOString() : "" })}
              testId="coupon-until" />
            <Input label="Max uses (global) — optional" type="number"
              value={draft.max_uses || ""} onChange={patch("max_uses")}
              testId="coupon-max" />
            <Input label="Max uses per user" type="number"
              value={draft.max_uses_per_user || 1}
              onChange={patch("max_uses_per_user")} testId="coupon-per-user" />
            <div className="md:col-span-2">
              <label className="text-[11px] font-heading uppercase tracking-widest text-ink-muted">Audience</label>
              <select value={draft.audience || ""} onChange={patch("audience")}
                className="mt-1 w-full px-3 py-2 rounded border border-navy/15 text-sm bg-white"
                data-testid="coupon-audience">
                <option value="">Both startups & investors</option>
                <option value="startup">Startups only</option>
                <option value="investor">Investors only</option>
              </select>
            </div>
          </div>
          <label className="inline-flex items-center gap-2 text-xs" data-testid="coupon-active-toggle">
            <input type="checkbox" checked={!!draft.is_active} onChange={patch("is_active")} />
            Active
          </label>
        </div>
        <div className="border-t border-navy/10 px-6 py-4 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onCancel} data-testid="coupon-cancel">Cancel</Button>
          <Button variant="gold" onClick={onSave} disabled={busy || !draft.code} data-testid="coupon-save">
            <Save size={14} className="inline mr-1" /> {busy ? "Saving…" : "Save coupon"}
          </Button>
        </div>
      </div>
    </div>
  );
}
